import { formatClock, parseWallClock } from '@/scheduling/lib/device-availability';

/**
 * Time-selection primitives shared by the PRACTITIONER rail
 * (`appointments/sections/time-date-selection-view.tsx`) and the BOOK-A-DEVICE
 * rail (`devices/book-device-panel.tsx`) — PROD-2455.
 *
 * Andrew, 2026-09-22: "we should have the custom slot as the provider one which
 * allow the user to enter the slot time" → "can we do them exactly like the
 * provider booking appointment?".
 *
 * The rule these helpers encode is the one the device rail was breaking: a
 * bookable start is something the SERVER says is free, never something the
 * client enumerates. Before PROD-2455 the device rail walked
 * `ResourceAvailability` windows in product-duration steps FROM THE WINDOW
 * START and dropped the past ones, so at 14:10 on a 10:00–23:00 window it
 * offered 15:00, 16:00, 17:00 … while the backend's own feed offered 14:15,
 * 15:15, 16:15 … — 45 minutes of genuinely free machine time the rail could
 * not even be asked for. Both rails now MAP a server list through
 * `toSlotItems` and present it through `groupSlotsByPartOfDay`; neither
 * derives one.
 *
 * Note on the API-layer ruling of 2026-09-13 ("device APIs mirror the
 * provider ones, share the engine not the endpoint"): that still holds and is
 * untouched — the two rails keep their two separate endpoints
 * (`Appointment/Slots` and `DeviceCalendarSlots`). It governs the wire. On the
 * UI layer PROD-2455 asks for the opposite and says so explicitly, which is
 * why this module exists at all.
 */

/**
 * One bookable start as the time-selection UI holds it.
 *
 * `start` / `end` stay the server's own LOCAL-CLOCK, no-`Z` strings
 * (`YYYY-MM-DDTHH:mm:ss`) rather than `Date`s, because that is exactly what
 * the book payload ships back on the wire — the backend reads the string
 * literally and applies the practice timezone itself. Parsing to a `Date` and
 * re-serialising is the timezone bug this codebase has already paid for twice
 * (see `shared/lib/date-format.ts` and the note in `CustomTimePopover`).
 *
 * `id` is synthetic and always carries the start: both feeds have been
 * observed shipping `00000000-0000-0000-0000-000000000000` as a slot id, which
 * collides across the whole strip and trips React's duplicate-key warning.
 */
export interface SlotItem {
  id: string;
  start: string;
  end: string;
}

/** The three buckets the practitioner rail has always grouped its slots into. */
export interface GroupedSlots {
  /** 00:00–11:59 */
  morning: SlotItem[];
  /** 12:00–16:59 */
  afternoon: SlotItem[];
  /** 17:00–23:59 */
  evening: SlotItem[];
}

/** The minimal shape both slot feeds return per entry. */
interface RawSlot {
  start?: string | null;
  end?: string | null;
}

/**
 * "9:00a" / "2:30p" — the chip label.
 *
 * The practitioner rail's `fmt12h` and the device rail's `formatClock` were
 * already character-for-character the same formatter over different input
 * types; this is the one that takes the wire string.
 */
export function formatSlotTime(iso: string): string {
  const d = parseWallClock(iso);
  return d ? formatClock(d) : '';
}

/**
 * The server's slot list, keyed for React and with past starts dropped.
 *
 * The past-start guard is the SECOND guard, not the first: both endpoints
 * already anchor today's grid at now. It earns its place anyway because a rail
 * left open across a slot boundary would otherwise keep offering a start that
 * went while the operator was searching for a patient — which is why the
 * device rail passes a ticking `useNow()` here rather than a frozen
 * `new Date()`.
 *
 * Boundary: a start EXACTLY equal to `now` is KEPT. That is the practitioner
 * rail's long-standing `>=` behaviour and PROD-2455 makes the device rail
 * match it; the device rail's own `bookableStarts(notBefore)` drops it
 * instead, and that asymmetry stays confined to `bookableStarts`, which after
 * this change no longer decides what either rail offers.
 *
 * Parsing is TEXTUAL (`parseWallClock`) rather than `new Date(iso)`: the slot
 * strings are local-clock with no offset today, but sibling fields on the very
 * same device response (`workingHours`) do print a `Z`, and a `Z` reaching
 * `new Date()` re-shifts the wall clock by the user's offset.
 */
export function toSlotItems(
  raw: readonly RawSlot[] | null | undefined,
  now: Date,
): SlotItem[] {
  const floor = now.getTime();
  return (raw ?? [])
    .map((s, i) => ({
      id: `${s?.start ?? ''}-${i}`,
      start: s?.start ?? '',
      end: s?.end ?? '',
    }))
    .filter((s) => {
      if (!s.start || !s.end) return false;
      const started = parseWallClock(s.start);
      return !!started && started.getTime() >= floor;
    });
}

/**
 * MORNING [00:00, 12:00) / AFTERNOON [12:00, 17:00) / EVENING [17:00, 24:00).
 *
 * The boundaries are the practitioner rail's, carried over unchanged: noon is
 * afternoon and 17:00 is evening. A slot whose start will not parse is dropped
 * rather than silently filed under Morning — an unparseable start is not a
 * bookable one.
 */
export function groupSlotsByPartOfDay(slots: readonly SlotItem[]): GroupedSlots {
  const morning: SlotItem[] = [];
  const afternoon: SlotItem[] = [];
  const evening: SlotItem[] = [];
  for (const s of slots) {
    const d = parseWallClock(s.start);
    if (!d) continue;
    const h = d.getHours();
    if (h < 12) morning.push(s);
    else if (h < 17) afternoon.push(s);
    else evening.push(s);
  }
  return { morning, afternoon, evening };
}
